import {
  canListingTransition,
  LISTING_STATUSES,
  LISTING_TRANSITIONS,
  resolveConsignmentFee,
  type Listing,
  type ListingStatus,
  type MarketplaceFees,
} from '@carsfinders/contracts';

/**
 * Moderation-queue domain logic (Phase 18 step 3), kept pure and separate from
 * the React page so every decision is unit-testable without a DOM.
 *
 * THE QUEUE IS SERVER-DRIVEN: `GET /api/admin/marketplace/listings?status=moderation`
 * returns the listings awaiting review, and {@link toQueueEntries} projects them
 * onto the rows below. It used to be a browser-local registry of ids typed in by
 * hand, because the API had no admin feed at all (the public browse serves
 * `active` only) — a moderator on a fresh browser saw an empty queue over a
 * backlog of pending listings.
 *
 * {@link mergeQueue} keeps a moderator's PINNED ids (added by hand, or just
 * acted on and therefore no longer matching the status filter) visible on top of
 * the fetched page. Every action still round-trips through the API, which
 * remains the source of truth for status.
 */

/**
 * The status filter of the queue tab: one status, or `all` — no `status` sent,
 * which the admin feed answers with every listing (published, drafts, …).
 */
export type QueueStatusFilter = ListingStatus | 'all';

/** The `status` query param for a filter value — `undefined` means every status. */
export function statusParam(filter: QueueStatusFilter): ListingStatus | undefined {
  return filter === 'all' ? undefined : filter;
}

/** The minimum rejection-reason length the API's rejectSchema enforces. */
export const MIN_REJECT_REASON_LENGTH = 10;

/** One queued listing awaiting moderation. */
export interface QueueEntry {
  listingId: string;
  /** Free-text moderator note (car, seller, why it is here). */
  note: string;
  /** ISO timestamp the entry was enqueued. */
  addedAt: string;
  /** Last status the API reported for this listing, when known. */
  lastKnownStatus?: ListingStatus;
  /**
   * The listing as the admin feed returned it. Present for every server-fed row;
   * absent only for an id the moderator pinned that the current filter misses.
   */
  listing?: Listing;
}

/** Listing ids are uuids/slugs — keep the accepted shape tight and path-safe. */
const LISTING_ID_PATTERN = /^[\w-]{1,64}$/;

/** True when `value` is a usable listing id. */
export function isValidListingId(value: string): boolean {
  return LISTING_ID_PATTERN.test(value.trim());
}

/**
 * Validate a rejection reason against the API contract (≥10 characters after
 * trimming). Returns the trimmed reason, or null when it is not acceptable.
 */
export function normalizeRejectReason(reason: string): string | null {
  const trimmed = reason.trim();
  return trimmed.length >= MIN_REJECT_REASON_LENGTH ? trimmed : null;
}

/**
 * Add an entry, or merge into the existing one when the id is already queued
 * (idempotent: a moderator pasting the same id twice must not duplicate a row).
 * Newest entries come first. Returns the unchanged queue for an invalid id.
 */
export function addToQueue(
  queue: readonly QueueEntry[],
  listingId: string,
  note = '',
  now: Date = new Date(),
): QueueEntry[] {
  const id = listingId.trim();
  if (!isValidListingId(id)) return [...queue];

  const existing = queue.find((entry) => entry.listingId === id);
  if (existing) {
    return queue.map((entry) =>
      entry.listingId === id
        ? { ...entry, note: note.trim() || entry.note }
        : entry,
    );
  }
  return [
    { listingId: id, note: note.trim(), addedAt: now.toISOString() },
    ...queue,
  ];
}

/** Drop one entry from the queue. */
export function removeFromQueue(
  queue: readonly QueueEntry[],
  listingId: string,
): QueueEntry[] {
  return queue.filter((entry) => entry.listingId !== listingId);
}

/** Record the status the API just reported for a queued listing. */
export function markQueueStatus(
  queue: readonly QueueEntry[],
  listingId: string,
  status: ListingStatus,
): QueueEntry[] {
  return queue.map((entry) =>
    entry.listingId === listingId
      ? { ...entry, lastKnownStatus: status }
      : entry,
  );
}

/**
 * Toggle one id in the bulk selection. Selection is kept as an id array (not a
 * Set) so it survives React state comparison and serialisation unchanged.
 */
export function toggleSelection(
  selected: readonly string[],
  listingId: string,
): string[] {
  return selected.includes(listingId)
    ? selected.filter((id) => id !== listingId)
    : [...selected, listingId];
}

/** The status a row shows: the fetched listing's, else the last one the API reported. */
export function entryStatus(entry: QueueEntry): ListingStatus | undefined {
  return entry.listing?.status ?? entry.lastKnownStatus;
}

/**
 * Whether a one-click action (Опублікувати → `active`, Повернути → `rejected`)
 * applies to a listing in `status`, per the shared transition table — so the
 * UI never offers a move the API answers with 400 (e.g. «Cannot move listing
 * from 'active' to 'active'» on an already published car). An unknown status
 * (a pinned id not fetched yet) stays allowed: the API decides.
 */
export function canQuickMove(status: ListingStatus | undefined, to: ListingStatus): boolean {
  return status === undefined || canListingTransition(status, to).ok;
}

/**
 * The ids a bulk action should run against: the current selection narrowed to
 * ids actually present in the queue, in queue order (so the moderator sees the
 * actions fire top-down). A stale selection can never act on a removed row.
 * With `to`, rows that cannot move there are left out too — «Схвалити вибрані»
 * over a mixed selection approves the pending ones and skips the published.
 */
export function bulkTargets(
  queue: readonly QueueEntry[],
  selected: readonly string[],
  to?: ListingStatus,
): string[] {
  return queue
    .filter((entry) => selected.includes(entry.listingId))
    .filter((entry) => to === undefined || canQuickMove(entryStatus(entry), to))
    .map((e) => e.listingId);
}

/** True when every queued row is selected (drives the select-all checkbox). */
export function isAllSelected(
  queue: readonly QueueEntry[],
  selected: readonly string[],
): boolean {
  return (
    queue.length > 0 &&
    queue.every((entry) => selected.includes(entry.listingId))
  );
}

/** Select every queued row, or clear the selection when all are already on. */
export function toggleSelectAll(
  queue: readonly QueueEntry[],
  selected: readonly string[],
): string[] {
  return isAllSelected(queue, selected)
    ? []
    : queue.map((entry) => entry.listingId);
}

/**
 * Statuses an admin may move a listing to, per the shared transition table.
 * Populates the transition modal so the UI cannot offer a move the API would
 * reject with 400.
 *
 * `adminOverride` widens this to every other status — the override path accepts
 * any from→to but demands a reason, which {@link isTransitionSubmittable} checks.
 */
export function allowedTransitions(
  from: ListingStatus,
  adminOverride = false,
): ListingStatus[] {
  if (!adminOverride) return [...(LISTING_TRANSITIONS[from] ?? [])];
  return LISTING_STATUSES.filter((to) => to !== from);
}

/**
 * Whether the transition modal may submit: delegates to the shared guard so the
 * UI and the API agree on forward-only moves and on the override reason being
 * mandatory.
 */
export function isTransitionSubmittable(
  from: ListingStatus,
  to: ListingStatus,
  options: { adminOverride?: boolean; reason?: string } = {},
): boolean {
  return canListingTransition(from, to, options).ok;
}

/**
 * Consignment fee preview. Own-stock carries no fee (the platform IS the
 * seller), so the mode gates the computation rather than the rate doing it.
 */
export function previewFee(
  fees: MarketplaceFees,
  salePrice: number,
  mode: 'own_stock' | 'consignment',
): number {
  if (mode === 'own_stock') return 0;
  if (!Number.isFinite(salePrice)) return 0;
  return resolveConsignmentFee(fees, salePrice);
}

// ── Server feed → queue rows ────────────────────────────────────────────────

/**
 * Project a page of admin-feed listings onto queue rows, preserving the order
 * the API returned (newest-created first). The note is left empty — a
 * server-fed row renders the real car instead of a moderator's memo.
 */
export function toQueueEntries(listings: readonly Listing[]): QueueEntry[] {
  return listings
    .filter(
      (listing) => typeof listing?.listingId === 'string' && listing.listingId !== '',
    )
    .map((listing) => ({
      listingId: listing.listingId,
      note: '',
      addedAt:
        listing.createdAt instanceof Date
          ? listing.createdAt.toISOString()
          : ((listing.createdAt as unknown as string | null) ?? ''),
      lastKnownStatus: listing.status,
      listing,
    }));
}

/**
 * Fold a freshly fetched page into the queue currently on screen.
 *
 * The SERVER page wins for every listing it contains (status and car data are
 * re-read, so an approve done in another tab shows up). Rows the page does NOT
 * contain are kept as PINNED entries and float to the top — that is the id a
 * moderator pasted by hand, or the listing they just approved, which has left
 * `status=moderation` and would otherwise vanish mid-interaction, taking its
 * "approved" feedback with it.
 */
export function mergeQueue(
  serverEntries: readonly QueueEntry[],
  currentQueue: readonly QueueEntry[],
): QueueEntry[] {
  const fetched = new Set(serverEntries.map((entry) => entry.listingId));
  const pinned = currentQueue.filter((entry) => !fetched.has(entry.listingId));
  return [...pinned, ...serverEntries];
}
